import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  CrowdctrlToastService,
  TenantSelectionService,
  WorkspaceSessionService,
} from '@crowdctrl/shared';
/**
 * Guard for the queue-forms route. Fetches queue selection forms at navigation time
 * to handle stale data (e.g. new forms created while user navigates from work tab).
 * - 0 forms: redirect to my-orders, show toast, block
 * - 1 form: redirect to queue-form/:formId, block
 * - 2+ forms: allow navigation to form selection list
 */
export const queueFormsGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const workspaceSessionService = inject(WorkspaceSessionService);
  const tenantSelectionService = inject(TenantSelectionService);
  const toastService = inject(CrowdctrlToastService);

  // Params are on parent route (workspaces/:id/sessions/:sessionId)
  const workspaceId = route.parent?.paramMap.get('id') ?? null;
  const sessionId = route.parent?.paramMap.get('sessionId') ?? null;

  if (!workspaceId || !sessionId) {
    router.navigate(['/workspaces']);
    return false;
  }

  const tenantId = await tenantSelectionService.getEffectiveTenantId();
  if (!tenantId) {
    router.navigate(['/workspaces', workspaceId, 'sessions', sessionId, 'my-orders']);
    return false;
  }

  const response = await firstValueFrom(
    workspaceSessionService.listSessionFormControls(sessionId, workspaceId, 100),
  );

  const queueSelectionForms = (response?.items ?? []).filter(
    (f) => (f.formType === 'queueSelection' || f.isSpecial === true) && f.enabled === true,
  );

  if (queueSelectionForms.length === 0) {
    await toastService.show({
      message: 'No queue forms available',
      duration: 3000,
      color: 'medium',
    });
    router.navigate(['/workspaces', workspaceId, 'sessions', sessionId, 'my-orders'], {
      replaceUrl: true,
    });
    return false;
  }

  if (queueSelectionForms.length === 1) {
    router.navigate(
      [
        '/workspaces',
        workspaceId,
        'sessions',
        sessionId,
        'my-orders',
        'queue-form',
        queueSelectionForms[0].id,
      ],
      { replaceUrl: true },
    );
    return false;
  }

  return true;
};
